<script>
import HistoryBadge from './HistoryBadge.vue'
import HistoryFishComposition from './HistoryFishComposition.vue'
import FishIcon from './FishIcon.vue'

export default {
	name: 'HistoryShipmentCard',
	components: { HistoryBadge, HistoryFishComposition, FishIcon },
	props: {
		// normalized shipment item (see history.vue normalizeItem)
		item: {
			type: Object,
			required: true,
		},
		unit: {
			type: String,
			default: 'кг',
		},
	},
	data() {
		return {
			expanded: false,
		}
	},
	methods: {
		toggle() {
			this.expanded = !this.expanded
		},
		fishSize(fish) {
			const n = Number(fish && fish.size)
			return isFinite(n) ? n : 0
		},
	},
}
</script>

<template>
	<div class="shipment-card" :class="{ 'shipment-card--expanded': expanded }">
		<div class="shipment-card__header" @click="toggle">
			<div class="shipment-card__lead">
				<div class="shipment-card__row">
					<button
						type="button"
						class="shipment-card__toggle"
						:class="{ 'shipment-card__toggle--open': expanded }"
						@click.stop="toggle"
					>
						<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M6 9l6 6 6-6"
								stroke="currentColor"
								stroke-width="2.4"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</button>

					<div class="shipment-card__parties">
						<div class="shipment-card__party">
							<div class="shipment-card__party-name">{{ item.sender.name }}</div>
							<div v-if="item.sender.bin" class="shipment-card__party-bin">
								{{ item.sender.bin }}
							</div>
						</div>
						<span class="shipment-card__arrow">
							<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M4 12h15m0 0l-5-5m5 5l-5 5"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</span>
						<div class="shipment-card__party">
							<div class="shipment-card__party-name">{{ item.receiver.name }}</div>
							<div v-if="item.receiver.bin" class="shipment-card__party-bin">
								{{ item.receiver.bin }}
							</div>
						</div>
					</div>
				</div>

				<div class="shipment-card__below">
					<div class="shipment-card__chips">
						<span v-for="(fish, i) in item.fishes" :key="i" class="shipment-card__chip">
							<span class="shipment-card__chip-icon">
								<fish-icon />
							</span>
							{{ fish.fish_name || fish.title || '-' }} · {{ fishSize(fish) }} {{ unit }}
						</span>
					</div>

					<div class="shipment-card__meta">
						<span class="shipment-card__nikad">{{ item.nikad }}</span>
						<span v-if="item.date" class="shipment-card__date"> · {{ item.date }}</span>
					</div>
				</div>
			</div>

			<div class="shipment-card__side" @click.stop>
				<div class="shipment-card__total">{{ item.total }} {{ unit }}</div>
				<history-badge
					:variant="item.statusVariant"
					:icon="item.statusVariant === 'green' ? 'check' : ''"
				>
					{{ item.statusLabel }}
				</history-badge>
				<history-badge variant="blue" :icon="item.direction === 'in' ? 'arrow-in' : 'arrow-out'">
					{{ item.directionLabel }}
				</history-badge>
			</div>
		</div>

		<transition name="shipment-expand">
			<div v-if="expanded" class="shipment-card__body" @click.stop>
				<div class="shipment-card__panel">
					<history-fish-composition
						:fishes="item.fishes"
						:total="Number(item.total)"
						:unit="unit"
						:ref-nikad="item.refNikad"
						:record-id="item.id"
					/>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped>
.shipment-card {
	background: linear-gradient(to bottom right, #eff6ff 0%, #ffffff 100%);
	border: 1px solid #d3e7fb;
	border-radius: 14px;
	box-shadow: 0 4px 18px rgba(21, 60, 110, 0.05);
	margin-bottom: 14px;
	transition: box-shadow 0.2s, border-color 0.2s;
}

.shipment-card:hover {
	border-color: #dbe6f5;
	box-shadow: 0 6px 22px rgba(21, 60, 110, 0.09);
}

.shipment-card--expanded {
	border-color: #cfe1fb;
}

.shipment-card__header {
	display: flex;
	align-items: flex-start;
	gap: 14px;
	padding: 25px 25px 15px 25px;
	cursor: pointer;
}

.shipment-card__toggle {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: none;
	background: #157fee;
	color: #ffffff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	outline: none;
	transition: transform 0.25s;
}
.shipment-card__toggle svg {
	width: 18px;
	height: 18px;
}
.shipment-card__toggle--open {
	transform: rotate(180deg);
}

.shipment-card__lead {
	flex: 1;
	min-width: 0;
}

.shipment-card__row {
	display: flex;
	align-items: center;
	gap: 14px;
}

.shipment-card__below {
	margin-top: 10px;
}

.shipment-card__parties {
	display: flex;
	align-items: center;
	gap: 69px;
	flex-wrap: wrap;
}

.shipment-card__party-name {
	font-family: 'Montserrat', sans-serif;
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0;
	color: #151d48;
	line-height: 1.25;
}

.shipment-card__party-bin {
	font-family: 'Montserrat', sans-serif;
	font-size: 13px;
	font-weight: 500;
	letter-spacing: 0;
	color: #475f84;
	line-height: 1.25;
	margin-top: 0;
}

.shipment-card__arrow {
	display: inline-flex;
	align-items: center;
	color: #b4c2d8;
}
.shipment-card__arrow svg {
	width: 22px;
	height: 22px;
}

.shipment-card__chips {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.shipment-card__chip {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	height: 28px;
	background: #d3e7fb;
	color: #475f84;
	font-family: 'Montserrat', sans-serif;
	font-size: 13px;
	font-weight: 500;
	letter-spacing: 0;
	padding: 0 8px;
	border-radius: 12px;
}

.shipment-card__chip-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #157fee;
}
.shipment-card__chip-icon svg {
	width: 16px;
	height: 16px;
}

.shipment-card__meta {
	margin-top: 10px;
	font-family: 'Montserrat', sans-serif;
	font-size: 13px;
	font-weight: 400;
	letter-spacing: 0;
	line-height: 1.25;
	color: #475f84;
}
.shipment-card__nikad {
	font-weight: 400;
	color: #475f84;
}

.shipment-card__side {
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 8px;
}

.shipment-card__total {
	font-size: 16px;
	font-weight: 600;
	color: #151d48;
	line-height: 1;
	margin-bottom: 2px;
}

.shipment-card__body {
	padding: 0 25px 25px 25px;
}

.shipment-card__panel {
	background: #ffffff;
	border-radius: 10px;
	padding: 16px 16px;
}

/* expand transition */
.shipment-expand-enter-active,
.shipment-expand-leave-active {
	transition: opacity 0.2s ease;
}
.shipment-expand-enter,
.shipment-expand-leave-to {
	opacity: 0;
}

@media (max-width: 992px) {
	.shipment-card__parties {
		gap: 28px;
	}
}

@media (max-width: 768px) {
	.shipment-card__header {
		flex-wrap: wrap;
		gap: 10px;
		padding: 16px 16px 12px 16px;
	}
	.shipment-card__lead {
		width: 100%;
		flex: 1 1 100%;
	}
	.shipment-card__parties {
		gap: 16px;
	}
	.shipment-card__side {
		flex-direction: row;
		align-items: center;
		width: 100%;
		justify-content: flex-start;
		gap: 10px;
		flex-wrap: wrap;
	}
	.shipment-card__total {
		margin-bottom: 0;
	}
	.shipment-card__body {
		padding: 0 16px 16px 16px;
	}
}

@media (max-width: 480px) {
	.shipment-card__parties {
		gap: 10px;
	}
	.shipment-card__party-name {
		font-size: 13px;
	}
	.shipment-card__arrow svg {
		width: 18px;
		height: 18px;
	}
}
</style>
